import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../styles/admin/AdminProjectForm.css';

const AdminProjectCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    excerpt: '',
    content: '',
    category: 'web-development',
    status: 'draft',
    priority: 'medium',
    featured: false,
    client_name: '',
    client_email: '',
    client_phone: '',
    location: '',
    start_date: '',
    end_date: '',
    completion_date: '',
    budget: '',
    progress: 0,
    technologies: '',
    team_members: '',
    notes: '',
    meta_title: '',
    meta_description: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [previewGallery, setPreviewGallery] = useState([]);

  const API_BASE_URL = "https://phucansolutions.vn" || '"https://phucansolutions.vn"';

  const categories = [
    { value: 'web-development', label: 'Phát triển Web' },
    { value: 'mobile-app', label: 'Ứng dụng di động' },
    { value: 'architecture', label: 'Kiến trúc xây dựng' },
    { value: 'landscape', label: 'Tiểu cảnh sân vườn' },
    { value: 'solar', label: 'Năng lượng mặt trời' },
    { value: 'iot-cctv', label: 'IoT & Camera' }
  ];

  const statuses = [
    { value: 'draft', label: 'Nháp' },
    { value: 'published', label: 'Đã xuất bản' },
    { value: 'archived', label: 'Lưu trữ' },
    { value: 'planning', label: 'Lên kế hoạch' },
    { value: 'in-progress', label: 'Đang thực hiện' },
    { value: 'completed', label: 'Hoàn thành' },
    { value: 'on-hold', label: 'Tạm dừng' }
  ];

  const priorities = [
    { value: 'low', label: 'Thấp' },
    { value: 'medium', label: 'Trung bình' },
    { value: 'high', label: 'Cao' },
    { value: 'urgent', label: 'Khẩn cấp' }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setPreviewImage(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleGalleryChange = (e) => {
    const files = Array.from(e.target.files);
    setGalleryFiles(files);
    
    // Create preview for gallery images
    const previews = [];
    files.forEach((file, index) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        previews[index] = e.target.result;
        if (previews.length === files.length) {
          setPreviewGallery([...previews]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const removeGalleryImage = (index) => {
    const newFiles = galleryFiles.filter((_, i) => i !== index);
    const newPreviews = previewGallery.filter((_, i) => i !== index);
    setGalleryFiles(newFiles);
    setPreviewGallery(newPreviews);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      alert('Vui lòng nhập tiêu đề dự án');
      return;
    }

    // Validate email and phone if provided
    if (formData.client_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.client_email)) {
      alert('Email khách hàng không hợp lệ');
      return;
    }

    if (formData.client_phone && !/^[0-9+\-\s()]{10,15}$/.test(formData.client_phone)) {
      alert('Số điện thoại khách hàng không hợp lệ');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('admin_token');
      
      // Prepare form data for submission
      const submitData = new FormData();
      
      // Add all form fields
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
          if (key === 'progress') {
            submitData.append(key, parseInt(formData[key]) || 0);
          } else if (key === 'budget') {
            submitData.append(key, parseFloat(formData[key]) || 0);
          } else {
            submitData.append(key, formData[key]);
          }
        }
      });

      // Add image file if selected
      if (selectedFile) {
        submitData.append('image', selectedFile);
      }

      // Add gallery files if selected
      if (galleryFiles.length > 0) {
        galleryFiles.forEach((file, index) => {
          submitData.append('gallery', file);
        });
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/projects`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // Don't set Content-Type for FormData, browser will set it automatically
        },
        body: submitData
      });

      const data = await response.json();

      if (response.ok && data.success) {
        alert('Tạo dự án thành công!');
        navigate('/admin/projects');
      } else {
        throw new Error(data.error || 'Lỗi khi tạo dự án');
      }
    } catch (error) {
      console.error('Error creating project:', error);
      alert('Lỗi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-project-form">
      <div className="form-header">
        <div className="header-content">
          <h1>➕ Tạo Dự Án Mới</h1>
          <p>Thêm dự án mới vào hệ thống</p>
        </div>
        <button 
          type="button" 
          onClick={() => navigate('/admin/projects')}
          className="back-btn"
        >
          ← Quay lại
        </button>
      </div>

      <form onSubmit={handleSubmit} className="project-form">
        {/* Basic Information */}
        <div className="form-section">
          <h3>📝 Thông tin cơ bản</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="title">Tiêu đề dự án *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                placeholder="Nhập tiêu đề dự án..."
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="excerpt">Mô tả ngắn</label>
              <textarea
                id="excerpt"
                name="excerpt"
                value={formData.excerpt}
                onChange={handleInputChange}
                rows="3"
                placeholder="Mô tả ngắn gọn về dự án..."
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="description">Mô tả chi tiết</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="4"
                placeholder="Mô tả chi tiết về dự án..."
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="content">Nội dung</label>
              <textarea
                id="content"
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                rows="6"
                placeholder="Nội dung đầy đủ của dự án..."
              />
            </div>
          </div>
        </div>

        {/* Project Image */}
        <div className="form-section">
          <h3>🖼️ Hình ảnh dự án</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="image">Hình ảnh đại diện</label>
              <input
                type="file"
                id="image"
                name="image"
                onChange={handleFileChange}
                accept="image/*"
                className="file-input"
              />
              {previewImage && (
                <div className="image-preview">
                  <img src={previewImage} alt="Preview" />
                  <button 
                    type="button" 
                    onClick={() => {
                      setSelectedFile(null);
                      setPreviewImage(null);
                    }}
                    className="remove-image-btn"
                  >
                    ✖️ Xóa ảnh
                  </button>
                </div>
              )}
              <small className="form-hint">Chấp nhận: JPG, PNG, GIF. Tối đa 5MB</small>
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="gallery">Thư viện ảnh (Gallery)</label>
              <input
                type="file"
                id="gallery"
                name="gallery"
                onChange={handleGalleryChange}
                accept="image/*"
                multiple
                className="file-input"
              />
              
              {/* Gallery Preview */}
              {previewGallery.length > 0 && (
                <div className="gallery-preview">
                  <p>Ảnh gallery ({previewGallery.length} ảnh):</p>
                  <div className="gallery-grid">
                    {previewGallery.map((preview, index) => (
                      <div key={index} className="gallery-item">
                        <img src={preview} alt={`Gallery ${index + 1}`} />
                        <button
                          type="button"
                          onClick={() => removeGalleryImage(index)}
                          className="remove-gallery-btn"
                        >
                          ✖️
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <small className="form-hint">Chọn nhiều ảnh để tạo thư viện. Chấp nhận: JPG, PNG, GIF. Tối đa 5MB mỗi ảnh.</small>
            </div>
          </div>
        </div>

        {/* Project Settings */}
        <div className="form-section">
          <h3>⚙️ Cài đặt dự án</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="category">Danh mục</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="status">Trạng thái</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
              >
                {statuses.map(status => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="priority">Ưu tiên</label>
              <select
                id="priority"
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
              >
                {priorities.map(priority => (
                  <option key={priority.value} value={priority.value}>
                    {priority.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="progress">Tiến độ (%)</label>
              <input
                type="number"
                id="progress"
                name="progress"
                value={formData.progress}
                onChange={handleInputChange}
                min="0"
                max="100"
                placeholder="0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="featured">
                <input
                  type="checkbox"
                  id="featured"
                  name="featured"
                  checked={formData.featured}
                  onChange={handleInputChange}
                />
                Dự án nổi bật
              </label>
            </div>
          </div>
        </div>

        {/* Client Information */}
        <div className="form-section">
          <h3>👤 Thông tin khách hàng</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="client_name">Tên khách hàng</label>
              <input
                type="text"
                id="client_name"
                name="client_name"
                value={formData.client_name}
                onChange={handleInputChange}
                placeholder="Nhập tên khách hàng..."
              />
            </div>

            <div className="form-group">
              <label htmlFor="client_email">Email khách hàng</label>
              <input
                type="email"
                id="client_email"
                name="client_email"
                value={formData.client_email}
                onChange={handleInputChange}
                placeholder="email@example.com"
              />
            </div>

            <div className="form-group">
              <label htmlFor="client_phone">Số điện thoại</label>
              <input
                type="tel"
                id="client_phone"
                name="client_phone"
                value={formData.client_phone}
                onChange={handleInputChange}
                placeholder="+84 123 456 789"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="location">Địa điểm</label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                placeholder="Nhập địa điểm dự án..."
              />
            </div>

            <div className="form-group">
              <label htmlFor="budget">Ngân sách (VNĐ)</label>
              <input
                type="number"
                id="budget"
                name="budget"
                value={formData.budget}
                onChange={handleInputChange}
                placeholder="0"
                step="1000"
              />
            </div>
          </div>
        </div>

        {/* Project Timeline */}
        <div className="form-section">
          <h3>📅 Thời gian dự án</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="start_date">Ngày bắt đầu</label>
              <input
                type="date"
                id="start_date"
                name="start_date"
                value={formData.start_date}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="end_date">Ngày kết thúc dự kiến</label>
              <input
                type="date"
                id="end_date"
                name="end_date"
                value={formData.end_date}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="completion_date">Ngày hoàn thành thực tế</label>
              <input
                type="date"
                id="completion_date"
                name="completion_date"
                value={formData.completion_date}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>

        {/* Technical Details */}
        <div className="form-section">
          <h3>🔧 Chi tiết kỹ thuật</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="technologies">Công nghệ sử dụng</label>
              <textarea
                id="technologies"
                name="technologies"
                value={formData.technologies}
                onChange={handleInputChange}
                rows="3"
                placeholder="React, Node.js, MySQL, Docker..."
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="team_members">Thành viên nhóm</label>
              <textarea
                id="team_members"
                name="team_members"
                value={formData.team_members}
                onChange={handleInputChange}
                rows="3"
                placeholder="Nguyễn Văn A (Leader), Trần Thị B (Developer)..."
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="notes">Ghi chú</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows="4"
                placeholder="Ghi chú và lưu ý về dự án..."
              />
            </div>
          </div>
        </div>

        {/* SEO Settings */}
        <div className="form-section">
          <h3>🔍 Cài đặt SEO</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="meta_title">Meta Title</label>
              <input
                type="text"
                id="meta_title"
                name="meta_title"
                value={formData.meta_title}
                onChange={handleInputChange}
                placeholder="Tiêu đề SEO..."
                maxLength="60"
              />
              <small className="form-hint">Tối đa 60 ký tự</small>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="meta_description">Meta Description</label>
              <textarea
                id="meta_description"
                name="meta_description"
                value={formData.meta_description}
                onChange={handleInputChange}
                rows="3"
                placeholder="Mô tả SEO..."
                maxLength="160"
              />
              <small className="form-hint">Tối đa 160 ký tự</small>
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/admin/projects')}
            className="cancel-btn"
          >
            Hủy
          </button>
          <button
            type="submit"
            disabled={loading}
            className="submit-btn"
          >
            {loading ? '🔄 Đang tạo...' : '✅ Tạo dự án'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminProjectCreate; 